/*
 * Copyright (c) 2026.
 * Spin in het Web B.V. Apeldoorn.
 * Deze software mag alleen gebruikt / bekeken / gewijzigd worden volgens de voorwaarden die zijn vastgelegd in een bijbehorende schriftelijke overeenkomst met Spin in het Web B.V.
 */

/**
 * Auteur: Jelmer Jellema - Spin in het Web B.V
 * Gegenereerd met Claude Code (AI), 21-08-2026
 *
 * Root-service voor het docentendashboard (golf 3f). Draagt twee dingen die niet in het paneel
 * zelf kunnen wonen: de open-state (de modeltoolbar zet hem, het paneel leest hem — twee
 * sibling-eilanden, dus een root-service) en de binnenkomende statistiek, waarvoor de service zelf
 * op de notify-berichten luistert.
 *
 * De gegevens worden niet meteen doorgezet maar op een klok gepubliceerd: het paneel rekent zijn
 * hele telling opnieuw uit zodra de momentopname wijzigt, en zonder rem zou dat bij elke actie
 * van elke student opnieuw gebeuren. Vandaar één `gegevens`-signal met alles erin; wat
 * binnenkomt wacht in de buffer.
 *
 * De veldnamen in de binnenkomende rijen (`model_id`, `targettype`) zijn die van het backend: het
 * zijn de kolommen van de groepeerquery in `controller/dashboard.js`. Ze worden hier bewust niet
 * omgezet.
 */
import {inject, Injectable, signal} from '@angular/core';
import {KeyedTimeout, Sihwlog} from 'sihw-ngx';
import {ApiService} from '../app/backend/api.service';

/**
 * Hoe lang binnengekomen gegevens blijven wachten voordat het paneel opnieuw telt.
 */
const PUBLICATIEVERTRAGING = 10000;

/**
 * De opgeslagen normtelling van een model: per categorie wat het model heeft en wat de norm
 * verwacht.
 */
export interface Normtelling {
    /** Per normcategorie het aantal elementen in het model en hoeveel daarvan fout zijn. */
    model: Record<string, {aantal?: number; fouten?: number}>;
    /** Per normcategorie het aantal dat de norm verwacht. */
    norm: Record<string, number>;
}

/**
 * De gegevens die het backend per model kent. `imgUrl` komt absoluut binnen: de basis-url is
 * transportkennis en wordt in de `ApiService` aangeplakt.
 */
export interface Dashboardv1Modelinfo {
    titel: string;
    user: string;
    imgUrl?: string | null;
    normtelling?: Normtelling | null;
}

/**
 * Eén rij uit de actielog-telling: hoe vaak een actie in een model voorkomt.
 */
export interface ActieRij {
    model_id: string;
    action: string;
    count: number;
}

/**
 * Eén rij uit de typetelling: hoe vaak er in een model iets met een elementtype is gedaan.
 */
export interface TypeRij {
    model_id: string;
    targettype: string;
    count: number;
}

/**
 * De gepubliceerde momentopname waar het paneel op rekent.
 */
export interface Dashboardv1Gegevens {
    /** De actietellingen per model. */
    acties: readonly ActieRij[];
    /** De typetellingen per model. */
    types: readonly TypeRij[];
    /** De modellen die op dit moment openstaan bij een student. */
    live: readonly string[];
    /** Titel, gebruiker, plaatje en laatste normtelling per model. */
    modelinfo: Readonly<Record<string, Dashboardv1Modelinfo>>;
    /**
     * Modellen waarvan het backend zei ze niet te kennen. Dat zijn verwijderde modellen: de
     * actielog blijft na het verwijderen staan (`model.verwijder` raakt hem niet), en ook de
     * meekijk-schaduwmodellen die het backend zelf opruimt zodra niemand meer meekijkt laten
     * hun regels achter. Ze tellen niet mee — een kolom `?` met tellingen van een model dat niet
     * meer bestaat zegt de docent niets.
     */
    onbekend: ReadonlySet<string>;
    /** Volgnummer van deze publicatie; dient ook als cache-buster voor het detailplaatje. */
    publicatie: number;
}

//de meekijk-keten die AngularJS hier registreerde (`Dashboardv1Acties`) is met golf 4c vervallen:
//het paneel roept `ModelbeheerService.meekijken` rechtstreeks aan

@Injectable({providedIn: 'root'})
export class Dashboardv1Service {

    protected readonly log = new Sihwlog('info', 'Dashboardv1Service');

    /**
     * De backendlaag: het abonnement op de statistiek en het ophalen van de modelgegevens.
     */
    private readonly api = inject(ApiService);

    /**
     * Of het paneel openstaat.
     */
    private readonly _open = signal(false);

    /**
     * De laatst gepubliceerde momentopname.
     */
    private readonly _gegevens = signal<Dashboardv1Gegevens>({
        acties: [],
        types: [],
        live: [],
        modelinfo: {},
        onbekend: new Set(),
        publicatie: 0
    });

    /**
     * Wat er sinds de vorige publicatie is binnengekomen. Bewust geen signals: dit is de
     * wachtkamer, niet de waarheid waar het paneel op rekent.
     */
    private ruweActies: readonly ActieRij[] = [];
    private ruweTypes: readonly TypeRij[] = [];
    private ruweLive: readonly string[] = [];

    /**
     * De verzamelde modelgegevens. Wordt aangevuld bij elke publicatie en als geheel
     * meegepubliceerd, zodat een binnengekomen normtelling de klok niet omzeilt.
     */
    private modelinfo: Record<string, Dashboardv1Modelinfo> = {};

    /**
     * Modellen die we hebben opgevraagd en die het backend niet kent. Ze worden niet nog een
     * keer opgevraagd — dat scheelt bij elke publicatie een zinloze ronde over dezelfde lijst.
     */
    private onbekend = new Set<string>();

    /**
     * De publicatieklok.
     */
    private readonly timer = new KeyedTimeout();

    /**
     * Of het paneel openstaat.
     */
    readonly open = this._open.asReadonly();

    /**
     * De laatst gepubliceerde momentopname.
     */
    readonly gegevens = this._gegevens.asReadonly();

    constructor() {
        //de vier notificaties waarmee het backend de statistiek bijwerkt, en het verzoek om ons na een
        //hapering opnieuw aan te melden. Tot golf 4e gaf de AngularJS-hoofdcontroller ze door
        this.api.onNotify<{acties: ActieRij[]; types: TypeRij[]; bijSubscribe?: boolean}>('dashboardlog')
            .subscribe(data => this.zetLog(data.acties, data.types, !!data.bijSubscribe));
        this.api.onNotify<string[]>('dashboardlive').subscribe(live => this.zetLive(live));
        this.api.onNotify<{model: string; telling: Normtelling}>('dashboardtelling')
            .subscribe(data => this.zetTelling(data.model, data.telling));
        this.api.onNotify('registratieVerzoek').subscribe(() => this.meldHerabonneren());
    }

    /* ******** Openen en sluiten ******** */

    /**
     * Opent het paneel en meldt zich aan voor de statistiek. Doet niets als het al openstaat.
     * De rechtencheck ligt bij de knop in de modeltoolbar, die zonder het `dashboard1`-recht
     * niet bestaat.
     */
    openen(): void {
        if (this._open()) {
            return;
        }
        this._open.set(true);
        void this.api.dashboardAbonneer();
    }

    /**
     * Sluit het paneel en meldt zich af. Doet niets als het al dicht is, zodat het meekijken
     * (`ModelbeheerService`) hem onvoorwaardelijk kan aanroepen.
     */
    sluiten(): void {
        if (!this._open()) {
            return;
        }
        this._open.set(false);
        this.timer.clear('publiceer');
        void this.api.dashboardZegOp();
    }

    /**
     * Het backend vraagt om een verse registratie (na een hapering). Alleen zin als we open
     * staan.
     */
    meldHerabonneren(): void {
        if (this._open()) {
            void this.api.dashboardAbonneer();
        }
    }

    /* ******** De spiegel: gevuld door de notify-abonnementen in de constructor ******** */

    /**
     * Er is een verse actielog-telling voor het hele project binnengekomen. Bij een telling die
     * op ons eigen abonnement volgt gaat hij meteen door — dan kijkt er iemand naar een leeg
     * scherm.
     */
    zetLog(acties: readonly ActieRij[], types: readonly TypeRij[], bijSubscribe: boolean): void {
        this.ruweActies = acties ?? [];
        this.ruweTypes = types ?? [];
        this.planPublicatie(bijSubscribe);
    }

    /**
     * De lijst met modellen die op dit moment openstaan is gewijzigd.
     */
    zetLive(live: readonly string[]): void {
        this.ruweLive = live ?? [];
        if (this._open()) {
            this.planPublicatie(false);
        }
    }

    /**
     * Er is een nieuwe normtelling voor een model opgeslagen. Alleen interessant voor een model
     * dat we al kennen; de rest komt bij de volgende modelinfo-ronde vanzelf mee.
     */
    zetTelling(modelId: string, telling: Normtelling | null): void {
        const info = this.modelinfo[modelId];
        if (info) {
            this.modelinfo = {...this.modelinfo, [modelId]: {...info, normtelling: telling}};
        }
        this.planPublicatie(false);
    }

    /* ******** De publicatieklok ******** */

    /**
     * Plant de eerstvolgende publicatie. Direct als er nog niets te zien is of als de gegevens
     * op ons eigen abonnement volgen; anders wachten we de klok af. Staat er al een publicatie
     * gepland, dan komt die er vanzelf.
     */
    private planPublicatie(direct: boolean): void {
        if (direct || !this._gegevens().publicatie) {
            void this.publiceer();
        } else if (!this.timer.check('publiceer')) {
            this.timer.set('publiceer', () => void this.publiceer(), PUBLICATIEVERTRAGING);
        }
    }

    /**
     * Vult eerst de ontbrekende modelgegevens aan en zet daarna de hele momentopname door. De
     * buffers worden pas ná het ophalen gelezen, zodat twee publicaties die elkaar overlappen
     * allebei de nieuwste stand doorzetten.
     *
     * Anders dan ng1 halen we de gegevens op voor álle modellen in de telling, niet alleen voor
     * de modellen die het huidige filter overhoudt: het filteren gebeurt nu in een berekening
     * zonder api-toegang, en zo staan de labels meteen goed als de gebruiker naar "Alles" of
     * naar een detail wisselt.
     */
    private async publiceer(): Promise<void> {
        this.timer.clear('publiceer');
        await this.vulModelinfoAan();
        this._gegevens.set({
            acties: this.ruweActies,
            types: this.ruweTypes,
            live: this.ruweLive,
            modelinfo: this.modelinfo,
            onbekend: this.onbekend,
            publicatie: this._gegevens().publicatie + 1
        });
    }

    /**
     * Haalt de modelgegevens op van de modellen in de wachtkamer die we nog niet kennen. Wat het
     * backend niet teruggeeft bestaat niet meer; dat onthouden we, zodat we er niet elke ronde
     * opnieuw om vragen en de telling ze kan overslaan. Bij een mislukte call onthouden we
     * niets — dan is onbekend alleen "nu even niet bereikbaar".
     */
    private async vulModelinfoAan(): Promise<void> {
        const ontbreekt = new Set<string>();
        const nogNodig = (modelId: string) => modelId && !this.modelinfo[modelId] && !this.onbekend.has(modelId);
        for (const rij of this.ruweActies) {
            if (nogNodig(rij.model_id)) ontbreekt.add(rij.model_id);
        }
        for (const rij of this.ruweTypes) {
            if (nogNodig(rij.model_id)) ontbreekt.add(rij.model_id);
        }
        if (!ontbreekt.size) {
            return;
        }
        try {
            const gevonden = await this.api.dashboardModelinfo([...ontbreekt]);
            this.modelinfo = {...this.modelinfo, ...gevonden};
            const verdwenen = [...ontbreekt].filter(modelId => !gevonden[modelId]);
            if (verdwenen.length) {
                this.log.debug(`Modellen bestaan niet meer, tellen niet mee`, verdwenen);
                this.onbekend = new Set([...this.onbekend, ...verdwenen]);
            }
        } catch (fout) {
            //volgende ronde opnieuw proberen; tot die tijd blijven deze modellen buiten beeld
            this.log.error('Ophalen modelinfo mislukt', fout);
        }
    }
}
